import { useState } from 'react';
import type { Id, Sprint } from '../../api/types';
import { Notice } from '../../components/ui/Notice';
import {
  CARRY_OVER_TO_BACKLOG,
  carryOverTargets,
  describeCarryOver,
  type CarryOverPreview,
} from './sprint-close';

/**
 * COMPLETE A SPRINT — the confirmation, and the whole point of PH-13.
 *
 * 🔴 THE MISSING BUTTON WAS THE SYMPTOM; THIS PANEL IS THE FEATURE. Closing a sprint is irreversible
 * (there is no `reopen` on the API) and it MOVES EVERY UNFINISHED TICKET somewhere else. An action
 * that does that without saying so first is the defect, so the consequence is stated in the largest
 * sentence on the panel, before the button that commits it.
 *
 * ⭐ THE DESTINATION IS A CHOICE, because the API makes it one — and the default is the backlog,
 * which is what the endpoint does with no body at all. ⛔ The option list is not "the planned
 * sprints": it is every sprint the API would ACCEPT, derived by exclusion in `carryOverTargets`.
 *
 * 🔴 MOBILE FIRST, 390px. The panel is a BOTTOM SHEET on a phone (`items-end`, rounded top corners,
 * full width) and a centred dialog from `sm:` up — the same shape `PromoteDialog` uses, for the same
 * reason: a centred card on a 390px screen puts the confirm button under the thumb of nobody. The
 * two buttons are 44px tall and full-width-stacked below `sm:`, so neither is a small target next to
 * a destructive one.
 */
export function CompleteSprintDialog({
  sprint,
  sprints,
  preview,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  sprint: Sprint;
  /** The project's sprints, for the carry-over choice. */
  sprints: readonly Sprint[];
  preview: CarryOverPreview;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (carryOverToSprintId: Id | null) => void;
}) {
  const [target, setTarget] = useState<string>(CARRY_OVER_TO_BACKLOG);
  const targets = carryOverTargets(sprints, sprint.id);

  const targetName =
    target === CARRY_OVER_TO_BACKLOG
      ? 'the Backlog'
      : (targets.find((candidate) => candidate.id === target)?.name ?? 'the chosen sprint');

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Complete sprint ${sprint.name}`}
        className="max-h-full w-full max-w-lg space-y-3 overflow-y-auto rounded-t-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4 sm:rounded-lg"
      >
        <h2 className="text-base font-semibold">Complete {sprint.name}?</h2>

        {/* 🔴 THE CONSEQUENCE, FIRST AND IN FULL. Not a tooltip, not fine print under the button. */}
        <p className="text-sm text-[color:var(--color-ink)]">{describeCarryOver(preview, targetName)}</p>

        <p className="text-xs text-[color:var(--color-ink-muted)]">
          The sprint becomes <strong>Closed</strong> and its committed and completed points are
          snapshotted as they stand — ⚠️ that snapshot is what the retrospective reports forever,
          because tickets keep moving afterwards. ⛔ There is no way to reopen a closed sprint, and
          unfinished work cannot be left inside one: it would be invisible in every backlog.
        </p>

        <div>
          <label
            className="block text-xs font-semibold"
            htmlFor="complete-sprint-carry-over"
          >
            Move unfinished tickets to
          </label>
          <select
            id="complete-sprint-carry-over"
            value={target}
            onChange={(event) => setTarget(event.target.value)}
            disabled={busy}
            className="mt-1 h-11 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 text-sm"
          >
            {/* ⭐ The API's own default, and the case that needs no request body at all. */}
            <option value={CARRY_OVER_TO_BACKLOG}>The Backlog — no sprint</option>
            {targets.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            {targets.length === 0
              ? 'This project has no other open sprint, so the Backlog is the only destination — a closed sprint cannot receive carried-over work.'
              : 'Closed sprints are not offered: the API refuses one, because it would bury the tickets in history.'}
          </p>
        </div>

        {/* ⛔ VERBATIM. `a 'closed' sprint cannot be closed; only an active sprint can` tells the
            reader somebody else got there first; "Could not complete the sprint" tells them nothing
            and invites a second attempt that will fail the same way. */}
        {error ? (
          <Notice tone="error" title="The API refused to complete this sprint">
            <p className="break-words">{error}</p>
          </Notice>
        ) : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-11 rounded border border-[color:var(--color-line)] px-3 text-sm disabled:opacity-50 sm:h-9"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(target === CARRY_OVER_TO_BACKLOG ? null : target)}
            disabled={busy}
            className="h-11 rounded bg-[color:var(--color-accent)] px-4 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50 sm:h-9"
          >
            {busy ? 'Completing…' : 'Complete sprint'}
          </button>
        </div>
      </div>
    </div>
  );
}
